import { EditableList, useEditableListField } from '@atendimento/detail/components/EditableList'
import { Cell, Grid } from 'bold-ui'
import { FieldStatusEnum, useListFieldStatus } from 'components/form'
import { Form, FormRenderProps } from 'components/form/core/Form'
import { Cid11SelectField } from 'components/form/field/select/Cid11SelectField'
import { Cid11SelectFieldModel } from 'components/form/field/select/Cid11SelectField/model-cid11'
import { useCallback } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'

import { Cid11PlanoModel } from '../model-plano'
import { intervencaoPlanoItemValidator } from '../validator-plano'
import { IntervencaoPlanoRow } from './IntervencaoPlanoRow'

const CID11_CAPITULO_MEDICINA_TRADICIONAL = '26'
const HINT_MESSAGE = 'Este campo destina-se ao registro de códigos de Medicina Tradicional da CID-11.'

const meta = metaPath<Cid11PlanoModel>()

export interface Cid11PlanoFieldProps {
  name: MetaArray<Cid11PlanoModel>
  controlModifiedStatus?: boolean
}

export function Cid11PlanoField(props: Readonly<Cid11PlanoFieldProps>) {
  const { name, controlModifiedStatus = false } = props

  const {
    handleRowChanged,
    handleSubmit,
    input: { value },
    removeItem,
    resetItemToInitialValue,
  } = useEditableListField({ name, isSameRow })

  const { fieldStatus, disabledValues } = useListFieldStatus({ name, controlModifiedStatus, isSameRow })

  const cid11IsEqual = useCallback(
    (cid11: Cid11SelectFieldModel) => (value || []).some((item) => item.cid11.id === cid11.id),
    [value]
  )

  const itemIsDisabled = useCallback(
    (cid11: Cid11SelectFieldModel) => disabledValues.some((item) => item.cid11.id === cid11.id),
    [disabledValues]
  )

  const rows: [Cid11PlanoModel, FieldStatusEnum?][] = controlModifiedStatus
    ? Array.from(fieldStatus)
    : (value || []).map((item) => [item])

  const renderForm = (formProps: FormRenderProps<Cid11PlanoModel>) => {
    const handleChange = (newValue: Cid11SelectFieldModel) => {
      newValue && formProps.form.submit()
    }

    return (
      <Cid11SelectField
        name={meta.cid11}
        label='CID 11'
        isCid10Required={false}
        capitulos={[CID11_CAPITULO_MEDICINA_TRADICIONAL]}
        hintMessage={HINT_MESSAGE}
        loadItemsOnOpen={false}
        onChange={handleChange}
        itemIsEqual={cid11IsEqual}
        itemIsDisabled={!isEmpty(disabledValues) ? itemIsDisabled : undefined}
      />
    )
  }

  return (
    <Grid>
      <Cell size={8}>
        <Form<Cid11PlanoModel> render={renderForm} validate={intervencaoPlanoItemValidator} onSubmit={handleSubmit} />
      </Cell>
      {!isEmpty(rows) && (
        <Cell size={12}>
          <EditableList>
            {rows.map(([item, status]) => (
              <IntervencaoPlanoRow
                key={item._id}
                model={item}
                label='CID 11'
                getDescricao={getDescricao}
                getCodigo={getCodigo}
                status={status}
                onChange={handleRowChanged}
                onRemove={removeItem}
                onReset={resetItemToInitialValue}
                controlModifiedStatus={controlModifiedStatus}
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

const isSameRow = (itemA: Cid11PlanoModel, itemB: Cid11PlanoModel) => itemA.cid11.id === itemB.cid11.id

const getDescricao = (item: Cid11PlanoModel) => item.cid11.nome.toUpperCase()

const getCodigo = (item: Cid11PlanoModel) => item.cid11.codigo
